import React, { Suspense, useEffect } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from '../redux/store';
import paths from '../config/paths';
import AppLoading from './AppLoading';
import MainLayout from './routeMainLayout';
import SingleTab from './routeSingleTab';
import Chats from '../screens/public/chats';
import { updateProfession } from '../screens/auth/login/reducer';

const Login = React.lazy(() => import('../screens/auth/login'));
const SelectProfession = React.lazy(() => import('../screens/auth/selectProfession'));
const Tabs = React.lazy(() => import('../screens/content/tabs'));
const Chat = React.lazy(() => import('../screens/content/chat'));

interface RouteSwitcherProps {
  isAuthenticated: boolean;
}

const AuthRoute = ({ isAuthenticated, children }: { isAuthenticated: boolean; children: React.ReactNode }) => {
  const location = useLocation();

  // Allow access to select-profession even when authenticated
  if (isAuthenticated && location.pathname !== paths.auth.selectProfession) {
    return <Navigate to="/" replace />;
  }
  return <>{children}</>;
};

const ProtectedRoute = ({ isAuthenticated, children }: { isAuthenticated: boolean; children: React.ReactNode }) => {
  const { currentUser } = useSelector((state: RootState) => state.auth);
  const dispatch = useDispatch();
  const savedProfession = localStorage.getItem('selectedProfession');

  useEffect(() => {
    // If we have a saved profession but the user's profession is not updated in the state
    // update it to prevent unnecessary redirects
    if (
      savedProfession &&
      (!('profession_updated' in currentUser?.user) || currentUser?.user?.profession_updated === 0)
    ) {
      dispatch(updateProfession(parseInt(savedProfession)));
    }
  }, [savedProfession, currentUser]);

  if (!isAuthenticated) {
    return <Navigate to={paths.auth.login} replace />;
  }

  // Check both the user state and localStorage to determine if profession is selected
  if (
    (!('profession_updated' in currentUser?.user) || currentUser?.user?.profession_updated === 0) &&
    !savedProfession
  ) {
    return <Navigate to={paths.auth.selectProfession} replace />;
  }

  return (
    <SingleTab>
      <MainLayout>{children}</MainLayout>
    </SingleTab>
  );
};

const RouteSwitcher: React.FC<RouteSwitcherProps> = ({ isAuthenticated }) => {
  const { currentUser } = useSelector((state: RootState) => state.auth);

  return (
    <Suspense fallback={<AppLoading />}>
      <Routes>
        {/* Public Routes (No MainLayout) */}
        <Route
          path={paths.auth.login}
          element={
            <AuthRoute isAuthenticated={isAuthenticated}>
              <Login />
            </AuthRoute>
          }
        />
        <Route
          path={paths.auth.selectProfession}
          element={
            <Suspense fallback={<AppLoading />}>
              <SelectProfession />
            </Suspense>
          }
        />
        <Route path="shared/chats/:chatId" element={<Chats />} />

        {/* Protected Routes (With MainLayout) */}
        <Route
          path={paths.content.tabs}
          element={
            <ProtectedRoute isAuthenticated={isAuthenticated}>
              <Tabs />
            </ProtectedRoute>
          }
        />
        <Route
          path=":tabId/chats"
          element={
            <ProtectedRoute isAuthenticated={isAuthenticated}>
              <Chat />
            </ProtectedRoute>
          }
        />
        <Route
          path=":tabId/chats/:chatId/:layout"
          element={
            <ProtectedRoute isAuthenticated={isAuthenticated}>
              <Chat />
            </ProtectedRoute>
          }
        />

        {/* Redirect all other paths */}
        <Route
          path="*"
          element={
            <Navigate
              to={
                isAuthenticated
                  ? currentUser?.user?.profession_updated === 0
                    ? paths.auth.selectProfession
                    : paths.content.tabs
                  : paths.auth.login
              }
              replace
            />
          }
        />
      </Routes>
    </Suspense>
  );
};

export default RouteSwitcher;
